import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useRouter } from '@tanstack/react-router';
import { useState } from 'react';
import { Button, ConfirmDialog, Field, Modal, Popover, Textarea } from '@bb/ui';
import { ApiError, type AdminApi, type CampaignAction, type CampaignSummary, type DependentView } from '../../api/client.ts';
import { usePermissions } from '../access/usePermissions.ts';
import { ObjectInUseDialog } from '../operations/ObjectInUseDialog.tsx';
import { incompleteTranslations, TranslationIncompleteNotice } from '../composer/shared/TranslationIncompleteNotice.tsx';

const LABELS: Record<CampaignAction, string> = { publish: 'Launch', pause: 'Pause', resume: 'Resume', stop: 'Stop', archive: 'Archive', restore: 'Restore', duplicate: 'Duplicate' };
/** Launching and changing what is sent need `messages.publish`; the rest `campaigns.edit` (the server checks again). */
const PERMISSION: Record<CampaignAction, string> = { publish: 'messages.publish', pause: 'messages.publish', resume: 'messages.publish', stop: 'messages.publish', archive: 'campaigns.edit', restore: 'campaigns.edit', duplicate: 'campaigns.edit' };

const errorText = (error: unknown) => (error instanceof ApiError ? error.message : 'Something went wrong.');

/**
 * Actions a campaign allows in its status (the `actions` the API sends), as a row menu or a toolbar. Stop asks for
 * confirmation; resuming a stopped campaign asks for a reason (only future sends run); archiving a campaign in use
 * lists what uses it.
 */
export function CampaignActions({ environmentId, campaign, layout = 'menu' }: { environmentId: string; campaign: CampaignSummary; layout?: 'menu' | 'toolbar' }) {
  const { api } = useRouter().options.context as { api: AdminApi };
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const permissions = usePermissions();
  const [confirmStop, setConfirmStop] = useState(false);
  const [resumeReason, setResumeReason] = useState<string | null>(null);
  const [inUse, setInUse] = useState<DependentView[] | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const run = useMutation({
    mutationFn: ({ action, reason }: { action: CampaignAction; reason?: string }) =>
      action === 'archive' ? api.archiveCampaign(environmentId, campaign.id) : api.campaignAction(environmentId, campaign.id, action, reason ? { reason } : undefined),
    onSuccess: async (result, { action }) => {
      setConfirmStop(false);
      setResumeReason(null);
      await queryClient.invalidateQueries({ queryKey: ['campaigns', environmentId] });
      if (action === 'duplicate') await navigate({ to: '/env/$environmentId/campaigns/$campaignId', params: { environmentId, campaignId: result.id } });
    },
    onError: (error) => {
      if (error instanceof ApiError && error.code === 'object_in_use') setInUse((error.problem.dependents as DependentView[] | undefined) ?? []);
    },
  });
  const allowed = campaign.actions.filter((a) => permissions.has(PERMISSION[a]));
  if (allowed.length === 0) return null;
  const start = (action: CampaignAction) => {
    setMenuOpen(false);
    if (action === 'stop') setConfirmStop(true);
    else if (action === 'resume' && campaign.status === 'stopped') setResumeReason('');
    else run.mutate({ action });
  };
  const buttons = allowed.map((action) => (
    <Button key={action} variant={action === 'publish' ? 'primary' : action === 'stop' ? 'danger' : 'ghost'} onClick={() => start(action)} disabled={run.isPending}>
      {LABELS[action]}
    </Button>
  ));
  return (
    <>
      {layout === 'toolbar' ? (
        <div className="toolbar" role="group" aria-label={`Actions for ${campaign.name}`}>
          {buttons}
        </div>
      ) : (
        <Popover open={menuOpen} onOpenChange={setMenuOpen} label={`Actions for ${campaign.name}`} trigger={<Button variant="ghost" aria-label={`Actions for ${campaign.name}`}>⋯</Button>}>
          <div className="menu">{buttons}</div>
        </Popover>
      )}
      {run.isError && !(run.error instanceof ApiError && run.error.code === 'object_in_use') && (incompleteTranslations(run.error) ? <TranslationIncompleteNotice error={run.error} /> : <p role="alert">{errorText(run.error)}</p>)}
      <ConfirmDialog open={confirmStop} onOpenChange={setConfirmStop} title={`Stop “${campaign.name}”?`} confirmLabel="Stop campaign" busy={run.isPending} onConfirm={() => run.mutate({ action: 'stop' })}>
        Pending sends are cancelled. Resuming later only runs future sends; skipped ones are not sent.
      </ConfirmDialog>
      <Modal
        open={resumeReason !== null}
        onOpenChange={(open) => !open && setResumeReason(null)}
        title={`Resume “${campaign.name}”`}
        description="Only future sends run; sends skipped while it was stopped are not sent. The reason goes to the audit log."
        footer={
          <>
            <Button variant="ghost" onClick={() => setResumeReason(null)}>
              Cancel
            </Button>
            <Button variant="primary" disabled={(resumeReason ?? '').trim().length < 3} loading={run.isPending} onClick={() => run.mutate({ action: 'resume', reason: resumeReason!.trim() })}>
              Resume
            </Button>
          </>
        }
      >
        <Field label="Reason">
          <Textarea value={resumeReason ?? ''} onChange={(e) => setResumeReason(e.target.value)} />
        </Field>
      </Modal>
      {inUse && <ObjectInUseDialog open onOpenChange={(open) => !open && setInUse(null)} what={`“${campaign.name}”`} dependents={inUse} />}
    </>
  );
}
